/**
 * @typedef {Object} Financiamento
 * @property {boolean} isFinanciamento
 * @property {number} prazo_financiamento_meses
 */

/**
 * @typedef {Object} saldo_correcao
 * @property {boolean} isSaldoCorrecao
 * @property {number} valor_saldo_correcao
 * @property {number} data_saldo
 */

/**
 * @typedef {Object} valor_lote
 * @property {boolean} isValorLote
 * @property {number} valor_sugerido
 * @property {number} porcentagem_desconto
 */

/**
 * @typedef {Object} CondicoesEspeciais
 * @property {boolean} temCondicao
 * @property {boolean} isIsencao
 * @property {Financiamento} financiamento
 * @property {saldo_correcao} saldo_correcao
 * @property {valor_lote} valor_lote
 * @property {string} descricao
 */

import { Field, Formik } from 'formik';
import React, { useEffect } from 'react';
import { Form, Modal, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import * as Yup from 'yup';
import Selector from 'react-select';
import MoneyField from '~/Components/Form/MoneyField';
import _isEmpty from 'lodash/isEmpty';
import { FaInfoCircle } from 'react-icons/fa';
import { format, formatarMonetario } from '~/Utils/Helpers';
import {
  CondicoesEspeciaisStatus,
  DATE_FORMAT_SERVER,
  TipoComissao,
} from '~/constant';
import moment from 'moment';
import {
  calcularValorSinalEsperado,
  calcularValorSinalIncorporadoraEsperado,
  recalcularValoresProporcionais,
} from '~/Pages/Vendas/helpers';

// Funções implementadas localmente
const removerZeros = value => {
  if (typeof value === 'string') {
    return value.replace(/^0+(?=\d)/, '');
  }
  return value;
};

/**
 * Função que adiciona "/" entre o mês e o ano que o usuário digita
 * @param {string} valor
 * @returns {string}
 */
const formatDataString = valor => {
  if (!valor) return '';

  if (valor && valor.includes('T')) {
    const date = new Date(valor);
    const mes = (date.getMonth() + 1).toString().padStart(2, '0'); // ajusta para 01-12
    const ano = date.getFullYear();
    return `${mes}/${ano}`;
  } else {
    const somenteNumeros = valor.replace(/\D/g, '');
    let valorCorrigido = somenteNumeros;
    if (somenteNumeros.length > 2) {
      valorCorrigido =
        somenteNumeros.slice(0, 2) + '/' + somenteNumeros.slice(2, 6);
    }
    return valorCorrigido;
  }
};
/**
 * Formata uma data para exibir apenas o dia e o mês por extenso em português.
 * Exemplo: "2025-05-12T00:00:00" retorna "12 de maio"
 *
 * @param {string} data - A data a ser formatada.
 * @returns {string} A data formatada no padrão "dia de mês".
 */
const formatDiaMes = data => {
  if (!data) return '';
  // Garante que o moment utilize o locale 'pt-br'
  return moment(data)
    .locale('pt-br')
    .format('D [de] MMMM');
};

const ModalCondicoesEspeciais = ({
  handleClose,
  show,
  adicionarCondicoes,
  condicoesEspeciaisExistentes,
  valorVenda,
  valorVendaEspecial,
  salvarSinal,
  valoresAlcada,
  travarVenda,
  condicaoEspecialRemovida,
  setCondicaoEspecialRemovida,
  ativosSelecionados,
  ativosDaTabelaSelecionadaPorCodigo,
  listaFormasDePagamento,
  addPagamentoContraProposta,
  prospecto,
  comissaoEmpreendimento,
}) => {
  let descricao, financiamento, isIsencao, saldo_correcao, valor_lote;
  if (condicoesEspeciaisExistentes) {
    descricao = condicoesEspeciaisExistentes.descricao;
    financiamento = condicoesEspeciaisExistentes.financiamento;
    isIsencao =
      condicoesEspeciaisExistentes.isIsencao ||
      condicoesEspeciaisExistentes.is_isencao;
    saldo_correcao = condicoesEspeciaisExistentes.saldo_correcao;
    valor_lote = condicoesEspeciaisExistentes.valor_lote;

    if (financiamento && financiamento.prazo_financiamento_meses > 0) {
      financiamento = {
        ...financiamento,
        isFinanciamento: true,
        termino_financiamento: formatDiaMes(
          condicoesEspeciaisExistentes.financiamento.data_prazo_financiamento
        ),
      };
    }

    if (saldo_correcao && saldo_correcao.valor_saldo_correcao > 0) {
      saldo_correcao = { ...saldo_correcao, isSaldoCorrecao: true };
    }

    if (valor_lote && valor_lote.porcentagem_desconto > 0) {
      valor_lote = { ...valor_lote, isValorLote: true };
    }
  }

  const initialValues = {
    isIsencao: isIsencao || false,
    financiamento: {
      isFinanciamento:
        (financiamento && financiamento.isFinanciamento) || false,
      prazo_financiamento_meses:
        (financiamento && financiamento.prazo_financiamento_meses) || 0,
      // Campo calculado com base na quantidade de meses
      termino_financiamento:
        (financiamento && financiamento.termino_financiamento) || '',
      data_prazo_financiamento:
        (financiamento && financiamento.data_prazo_financiamento) || '',
    },
    saldo_correcao: {
      isSaldoCorrecao:
        (saldo_correcao && saldo_correcao.isSaldoCorrecao) || false,
      valor_saldo_correcao:
        (saldo_correcao && saldo_correcao.valor_saldo_correcao) || 0.0,
      data_saldo:
        (saldo_correcao && formatDataString(saldo_correcao.data_saldo)) || '',
    },
    valor_lote: {
      isValorLote: (valor_lote && valor_lote.isValorLote) || false,
      valor_sugerido: (valor_lote && valor_lote.valor_sugerido) || 0.0,
      porcentagem_desconto:
        (valor_lote && valor_lote.porcentagem_desconto) || 0,
    },
    descricao: descricao || '',
    status:
      (condicoesEspeciaisExistentes && condicoesEspeciaisExistentes.status) ||
      0,
  };

  const validationSchema = Yup.object()
    .shape({
      isIsencao: Yup.boolean(),
      financiamento: Yup.object().shape({
        isFinanciamento: Yup.boolean(),
        prazo_financiamento_meses: Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .when('isFinanciamento', {
            is: true,
            then: Yup.number()
              .min(1, 'O valor deve ser maior que zero')
              .required('Informe o prazo do financiamento'),
            otherwise: Yup.number().notRequired(),
          }),
      }),
      saldo_correcao: Yup.object().shape({
        valor_saldo_correcao: Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .when('isSaldoCorrecao', {
            is: true,
            then: Yup.number()
              .min(0.01, 'O valor deve ser maior que zero')
              .required('Informe o saldo de correção'),
            otherwise: Yup.number().notRequired(),
          }),
        data_saldo: Yup.string()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .when('isSaldoCorrecao', {
            is: true,
            then: Yup.string()
              .matches(/^(0[1-9]|1[0-2])\/\d{4}$/, 'Data inválida')
              .required('Informe uma data'),
            otherwise: Yup.string().notRequired(),
          }),
      }),
      valor_lote: Yup.object().shape({
        valor_sugerido: Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .when('isValorLote', {
            is: true,
            then: Yup.number()
              .min(0.01, 'O valor deve ser maior que zero')
              .required('Informe um valor'),
            otherwise: Yup.number().notRequired(),
          }),
        porcentagem_desconto: Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .when('isValorLote', {
            is: true,
            then: Yup.number()
              .min(0.1, 'O valor deve ser maior que zero')
              .required('Informe uma porcentagem para o desconto'),
            otherwise: Yup.number().notRequired(),
          }),
      }),
      descricao: Yup.string()
        .trim()
        .required('Informe uma descrição')
        .test(
          'min-non-space',
          'A descrição deve ter no mínimo 20 caracteres',
          (value = '') => value.replace(/\s/g, '').length >= 20
        ),
    })
    .test(
      'at-least-one-option',
      'Preencha pelo menos uma opção além da descrição',
      function(value) {
        if (value) {
          const {
            isIsencao,
            financiamento,
            saldo_correcao,
            valor_lote,
          } = value;
          if (
            isIsencao ||
            (financiamento && financiamento.isFinanciamento) ||
            (saldo_correcao && saldo_correcao.isSaldoCorrecao) ||
            (valor_lote && valor_lote.isValorLote)
          ) {
            return true;
          }
        }
        return this.createError({
          path: 'descricao',
          message: 'Preencha pelo menos uma opção além da descrição',
        });
      }
    );

  /**
   * Calcula a data de término do financiamento com base no número de meses
   * @param {number} meses
   */
  // Base da carência: última parcela do sinal, não hoje. Mesma base de Vendas/Sinal e do servidor.
  const obterBaseCarencia = () => {
    const vencimentos = (listaFormasDePagamento || [])
      .map(pagamento =>
        moment.isMoment(pagamento.vencimento)
          ? moment(pagamento.vencimento)
          : moment(pagamento.vencimento, 'DD/MM/YYYY')
      )
      .filter(data => data.isValid());

    if (!vencimentos.length) return moment();
    // Clonado: moment.max devolve um elemento do array e o .add do chamador é mutante.
    return moment(moment.max(vencimentos));
  };

  const handleMesesChange = meses => {
    const m = Number(meses);
    if (!isNaN(m) && m > 0) {
      const dataCalculada = obterBaseCarencia().add(m, 'months');
      const dateParaInput = dataCalculada.format('MMMM [de] YYYY');
      const dateParaRequest = dataCalculada.format(DATE_FORMAT_SERVER);

      return { dateParaInput, dateParaRequest };
    }
    return '';
  };

  const obterPreviewCarencia = meses => {
    const m = Number(meses);
    if (isNaN(m) || m <= 0) return null;
    return obterBaseCarencia()
      .add(m, 'months')
      .format('DD/MM/YYYY');
  };

  const calcularValorComDesconto = (
    porcentagemDesconto,
    devolverDesconto = false
  ) => {
    const valorDoDesconto = (porcentagemDesconto * valorVenda) / 100;
    if (devolverDesconto) return valorDoDesconto;
    return valorVenda - valorDoDesconto;
  };

  const calcularPorcentagemDesconto = valorSugerido => {
    const desconto = valorVenda - valorSugerido;
    return Math.abs((desconto / valorVenda) * 100).toFixed(3);
  };

  const definirSinalParaModificacao = ativoEmEdicao => {
    if (comissaoEmpreendimento === TipoComissao.RetidaCorretor) {
      if (ativoEmEdicao.ge_vbp === 'S') {
        return 'valor_sinal_imobiliaria';
      }
      if (ativoEmEdicao.valor_sinal_incorporadora_original > 0) {
        return 'valor_sinal_incorporadora';
      }
      return 'valor_sinal_imobiliaria';
    }
  };

  const handleSubmitForm = values => {
    let valorDoDescontoEdicao = null;
    if (
      values.valor_lote.porcentagem_desconto ||
      (values.valor_lote.porcentagem_desconto &&
        values.status == CondicoesEspeciaisStatus.PendenteDiretoria)
    ) {
      valorDoDescontoEdicao = calcularValorComDesconto(
        values.valor_lote.porcentagem_desconto,
        true
      );
    }
    adicionarCondicoes(values);

    if (values.isIsencao) {
      travarVenda({
        travarCorretor: true,
        travarGerente: true,
      });
    } else if (
      (values.valor_lote &&
        values.valor_lote.porcentagem_desconto >
          valoresAlcada.percentual_gerente) ||
      (values.financiamento &&
        values.financiamento.prazo_financiamento_meses >
          valoresAlcada.meses_gerente)
    ) {
      travarVenda({
        travarCorretor: true,
        travarGerente: true,
      });
    } else if (
      (values.valor_lote &&
        values.valor_lote.porcentagem_desconto >
          valoresAlcada.percentual_corretor) ||
      (values.financiamento &&
        values.financiamento.prazo_financiamento_meses >
          valoresAlcada.meses_corretor)
    ) {
      travarVenda({
        travarCorretor: true,
        travarGerente: false,
      });
    } else {
      travarVenda({
        travarCorretor: false,
        travarGerente: false,
      });
    }
    if (valorVendaEspecial) {
      const codigoChave = Object.keys(ativosDaTabelaSelecionadaPorCodigo).find(
        item => item == ativosSelecionados[0]
      );
      const itemSelecionado = ativosDaTabelaSelecionadaPorCodigo[codigoChave];

      const novoValorVenda = values.valor_lote.valor_sugerido || valorVendaEspecial.valor_venda_original;
      
      const valorSinalEsperado = calcularValorSinalEsperado(
        {
          tipo_entrada: valorVendaEspecial.tipo_entrada,
          entrada_base: valorVendaEspecial.entrada_base,
        },
        novoValorVenda
      );

      // Aplica o recálculo proporcional quando o valor do lote é alterado
      let valoresRecalculados = {};
      if (values.valor_lote.valor_sugerido && itemSelecionado) {
        valoresRecalculados = recalcularValoresProporcionais(itemSelecionado, novoValorVenda);
      }

      if (comissaoEmpreendimento === TipoComissao.RetidaCorretor) {
        switch (definirSinalParaModificacao(itemSelecionado)) {
          case 'valor_sinal_imobiliaria':
            ativosDaTabelaSelecionadaPorCodigo[codigoChave].valor_sinal_imobiliaria = 
              valoresRecalculados.valor_sinal_imobiliaria || valorSinalEsperado;
            break;
          case 'valor_sinal_incorporadora':
            ativosDaTabelaSelecionadaPorCodigo[codigoChave].valor_sinal_incorporadora = 
              valoresRecalculados.valor_sinal_incorporadora || 
              calcularValorSinalIncorporadoraEsperado(itemSelecionado, novoValorVenda);
        }
      }

      salvarSinal({
        ...valorVendaEspecial,
        valor_venda: novoValorVenda,
        valor_sinal: valorSinalEsperado,
        valor_sinal_imobiliaria:
          valoresRecalculados.valor_sinal_imobiliaria ||
          ativosDaTabelaSelecionadaPorCodigo[codigoChave].valor_sinal_imobiliaria,
        valor_sinal_incorporadora:
          valoresRecalculados.valor_sinal_incorporadora ||
          ativosDaTabelaSelecionadaPorCodigo[codigoChave].valor_sinal_incorporadora,
        desconto_valor_venda: valorDoDescontoEdicao || 0,
        codigo: ativosSelecionados[0],
        alteracao_solicitacao_desconto: true,
      });
    } else {
      const codigoChave = Object.keys(ativosDaTabelaSelecionadaPorCodigo).find(
        item => item == ativosSelecionados[0]
      );
      const itemSelecionado = ativosDaTabelaSelecionadaPorCodigo[codigoChave];
      if (itemSelecionado && itemSelecionado.codigo) {
        const novoValorVenda = values.valor_lote.valor_sugerido || itemSelecionado.valor_venda_original;
        
        const valorSinalEsperado = calcularValorSinalEsperado(
          {
            tipo_entrada: itemSelecionado.tipo_entrada,
            entrada_base: itemSelecionado.entrada_base,
          },
          novoValorVenda
        );

        // Aplica o recálculo proporcional quando o valor do lote é alterado
        let valoresRecalculados = {};
        if (values.valor_lote.valor_sugerido) {
          valoresRecalculados = recalcularValoresProporcionais(itemSelecionado, novoValorVenda);
        }

        salvarSinal({
          ...itemSelecionado,
          valor_venda: novoValorVenda,
          valor_sinal: valorSinalEsperado,
          valor_sinal_imobiliaria: valoresRecalculados.valor_sinal_imobiliaria || itemSelecionado.valor_sinal_imobiliaria,
          valor_sinal_incorporadora: valoresRecalculados.valor_sinal_incorporadora || itemSelecionado.valor_sinal_incorporadora,
          desconto_valor_venda: valorDoDescontoEdicao || 0,
          codigo: itemSelecionado.codigo,
        });
      }
    }

    if (values.status == CondicoesEspeciaisStatus.PendenteDiretoria) {
      addPagamentoContraProposta(listaFormasDePagamento);
    }
    handleClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
      enableReinitialize={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        resetForm,
        handleSubmit,
      }) => (
        <Modal
          show={show}
          onHide={handleClose}
          onExit={resetForm}
          size="lg"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Adicionar condições especiais</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="isIsencao">
                <small>Selecione se haverá isenção da taxa de cessão</small>
                <Selector
                  isDisabled={
                    prospecto &&
                    prospecto.codigo_perfil_comprador !== 1 &&
                    prospecto.codigo_perfil_comprador !== 3
                  }
                  placeholder="Selecione uma opção para a taxa"
                  options={[
                    { value: 'sim', label: 'Sim' },
                    { value: 'nao', label: 'Não' },
                  ]}
                  value={
                    values.isIsencao
                      ? { value: 'sim', label: 'Sim' }
                      : { value: 'nao', label: 'Não' }
                  }
                  onChange={option =>
                    setFieldValue('isIsencao', option.value === 'sim')
                  }
                />
              </Form.Group>

              <Form.Group controlId="financiamento.isFinanciamento">
                <Form.Check
                  type="checkbox"
                  label="Carência do financiamento"
                  name="financiamento.isFinanciamento"
                  onChange={e => {
                    handleChange(e);
                    if (!e.target.checked) {
                      setFieldValue(
                        'financiamento.prazo_financiamento_meses',
                        0
                      );
                      setFieldValue('financiamento.termino_financiamento', '');
                    }
                  }}
                  checked={values.financiamento.isFinanciamento}
                />
              </Form.Group>
              {values.financiamento.isFinanciamento && (
                <div className="row shadow-sm p-1 mb-4 bg-white rounded">
                  <Form.Group
                    controlId="financiamento.prazo_financiamento_meses"
                    className="col-sm-12 col-md-4"
                  >
                    <div className="d-flex justify-content-between">
                      <small>Carência entre sinal e fin.(Meses)</small>
                    </div>
                    <Form.Control
                      type="number"
                      name="financiamento.prazo_financiamento_meses"
                      value={values.financiamento.prazo_financiamento_meses}
                      isInvalid={
                        errors.financiamento &&
                        errors.financiamento.prazo_financiamento_meses
                      }
                      onChange={e => {
                        const valorCorreto = removerZeros(e.target.value);
                        setFieldValue(
                          'financiamento.prazo_financiamento_meses',
                          valorCorreto
                        );
                        const {
                          dateParaInput,
                          dateParaRequest,
                        } = handleMesesChange(valorCorreto);
                        setFieldValue(
                          'financiamento.termino_financiamento',
                          dateParaInput
                        );
                        setFieldValue(
                          'financiamento.data_prazo_financiamento',
                          dateParaRequest
                        );
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.financiamento &&
                        errors.financiamento.prazo_financiamento_meses}
                    </Form.Control.Feedback>
                    {obterPreviewCarencia(
                      values.financiamento.prazo_financiamento_meses
                    ) && (
                      <small className="form-text text-muted">
                        Primeira parcela do financiamento a partir de{' '}
                        <strong>
                          {obterPreviewCarencia(
                            values.financiamento.prazo_financiamento_meses
                          )}
                        </strong>
                      </small>
                    )}
                  </Form.Group>
                </div>
              )}

              <Form.Group controlId="saldo_correcao.isSaldoCorrecao">
                <Form.Check
                  type="checkbox"
                  label="Saldo com Correção"
                  name="saldo_correcao.isSaldoCorrecao"
                  onChange={e => {
                    handleChange(e);
                    if (!e.target.checked) {
                      setFieldValue('saldo_correcao.valor_saldo_correcao', 0.0);
                      setFieldValue('saldo_correcao.data_saldo', '');
                    }
                  }}
                  checked={values.saldo_correcao.isSaldoCorrecao}
                />
              </Form.Group>
              {values.saldo_correcao.isSaldoCorrecao && (
                <div className="row shadow-sm p-1 mb-4 bg-white rounded">
                  <Form.Group
                    controlId="saldo_correcao.valor_saldo_correcao"
                    className="col-sm-12 col-md-4"
                  >
                    <small>Saldo correção</small>
                    <Field
                      name="saldo_correcao.valor_saldo_correcao"
                      className={`form-control ${
                        errors.saldo_correcao &&
                        errors.saldo_correcao.valor_saldo_correcao
                          ? 'is-invalid'
                          : ''
                      }`}
                      type="tel"
                      component={MoneyField}
                      validate={value =>
                        value <= 0 ? 'Informe um valor' : undefined
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.saldo_correcao &&
                        errors.saldo_correcao.valor_saldo_correcao}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group
                    controlId="saldo_correcao.data_saldo"
                    className="col-sm-12 col-md-4"
                  >
                    <small>Data saldo</small>
                    <Form.Control
                      placeholder="02/2000"
                      type="text"
                      name="saldo_correcao.data_saldo"
                      value={values.saldo_correcao.data_saldo}
                      isInvalid={
                        errors.saldo_correcao &&
                        errors.saldo_correcao.data_saldo
                      }
                      onChange={e => {
                        handleChange(e);
                        const valorCorrigido = formatDataString(e.target.value);
                        setFieldValue(
                          'saldo_correcao.data_saldo',
                          valorCorrigido
                        );
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.saldo_correcao &&
                        errors.saldo_correcao.data_saldo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}

              <Form.Group controlId="valor_lote.isValorLote">
                <Form.Check
                  type="checkbox"
                  label="Valor do Lote"
                  name="valor_lote.isValorLote"
                  onChange={e => {
                    handleChange(e);
                    if (!e.target.checked) {
                      setFieldValue('valor_lote.valor_sugerido', 0.0);
                      setFieldValue('valor_lote.porcentagem_desconto', 0);
                    }
                  }}
                  checked={values.valor_lote.isValorLote}
                />
              </Form.Group>
              {values.valor_lote.isValorLote && (
                <div className="row shadow-sm p-1 mb-4 bg-white rounded">
                  <Form.Group
                    controlId="valor_lote.valor_sugerido"
                    className="col-sm-12 col-md-4"
                  >
                    <small>Valor sugerido</small>
                    <Field
                      name="valor_lote.valor_sugerido"
                      className={`form-control ${
                        errors.valor_lote && errors.valor_lote.valor_sugerido
                          ? 'is-invalid'
                          : ''
                      }`}
                      type="tel"
                      component={MoneyField}
                      validate={value =>
                        value <= 0 ? 'Informe um valor' : undefined
                      }
                      onChange={e => {
                        let valorMonetario = e.replace(/[R$\s]/g, '');
                        valorMonetario = valorMonetario
                          .replace(/\./g, '')
                          .replace(',', '.');
                        const valorNumerico = parseFloat(valorMonetario);
                        const novaPorcentagem = calcularPorcentagemDesconto(
                          valorNumerico
                        );
                        setFieldValue(
                          'valor_lote.valor_sugerido',
                          valorNumerico
                        );
                        setFieldValue(
                          'valor_lote.porcentagem_desconto',
                          novaPorcentagem
                        );
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.valor_lote && errors.valor_lote.valor_sugerido}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group
                    controlId="valor_lote.porcentagem_desconto"
                    className="col-sm-12 col-md-4"
                  >
                    <small>Porcentagem de desconto </small>
                    <Form.Control
                      placeholder="(%)"
                      type="number"
                      name="valor_lote.porcentagem_desconto"
                      value={values.valor_lote.porcentagem_desconto}
                      onChange={e => {
                        const valorCorreto = removerZeros(e.target.value);
                        setFieldValue(
                          'valor_lote.porcentagem_desconto',
                          valorCorreto
                        );
                      }}
                      isInvalid={
                        errors.valor_lote &&
                        errors.valor_lote.porcentagem_desconto
                      }
                      onBlur={() => {
                        setFieldValue(
                          'valor_lote.valor_sugerido',
                          calcularValorComDesconto(
                            values.valor_lote.porcentagem_desconto
                          )
                        );
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.valor_lote &&
                        errors.valor_lote.porcentagem_desconto}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}

              {/* Descrição */}
              <Form.Group controlId="descricao">
                <small>Descrição da solicitação</small>
                <Form.Control
                  as="textarea"
                  name="descricao"
                  placeholder="..."
                  value={values.descricao}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={errors.descricao}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.descricao}
                </Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" type="submit">
                Salvar
              </Button>
              <Button variant="link" type="button" onClick={handleClose}>
                Cancelar
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default ModalCondicoesEspeciais;
